import { useEffect, useState } from 'react'
import { Header } from 'components/layout/header'
import { Main } from 'components/layout/main'
import { Search } from 'components/search'
import { GenericDataTable } from 'helpers/generic-data-table'
import { playersRequestsColumns } from 'helpers/players-requests-columns'
import { Loader } from 'lucide-react'
import {
  fetchPlayersRequests,
  approvePlayerRequest,
  rejectPlayerRequest,
} from 'store/playersRequests/playersRequestsSlice'
import { useAppDispatch, useAppSelector } from 'store/store'

const PlayersRequestsPage = () => {
  const dispatch = useAppDispatch()
  const { loading, error, playersRequests, totalPages } = useAppSelector(
    (state) => state.playersRequests
  )

  const [pageIndex, setPageIndex] = useState(0)
  const [statusFilter, setStatusFilter] = useState<string>('')
  const [notesFilter, setNotesFilter] = useState<string>('') // <--- Stato per la ricerca note
  const pageSize = 20

  useEffect(() => {
    // Implementiamo un piccolo "debounce": aspetta mezzo secondo che
    // l'utente finisca di scrivere prima di fare la chiamata API
    const delayDebounceFn = setTimeout(() => {
      dispatch(
        fetchPlayersRequests({
          page: pageIndex + 1,
          status: statusFilter,
          notes: notesFilter,
        })
      )
    }, 400)

    return () => clearTimeout(delayDebounceFn)
  }, [dispatch, pageIndex, statusFilter, notesFilter])

  const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setStatusFilter(e.target.value)
    setPageIndex(0)
  }

  const handleNotesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNotesFilter(e.target.value)
    setPageIndex(0)
  }

  let content = <Loader className='h-5 w-5 animate-spin' />

  if (!loading) {
    if (error) {
      content = (
        <div className='uk-alert uk-alert-danger text-center'>{error}</div>
      )
    } else if (playersRequests) {
      content = (
        <GenericDataTable
          columns={playersRequestsColumns({
            onApprove: (requestId, notes) => {
              dispatch(approvePlayerRequest({ requestId, notes }))
            },
            onReject: (requestId, notes) => {
              dispatch(rejectPlayerRequest({ requestId, notes }))
            },
          })}
          data={playersRequests || []}
          pageCount={totalPages}
          pageIndex={pageIndex}
          pageSize={pageSize}
          onPageIndexChange={setPageIndex}
        />
      )
    }
  }

  return (
    <>
      <Header>
        <div className='ml-auto flex items-center space-x-4'>
          <Search />
        </div>
      </Header>

      <Main>
        <div className='mb-4 flex flex-col justify-between space-y-4 md:flex-row md:items-center md:space-y-0'>
          <div>
            <h2 className='text-2xl font-bold tracking-tight'>
              Richieste Giocatore
            </h2>
            <p className='text-muted-foreground'>
              Richieste create dai giocatori per associare una tessera FIT.
            </p>
          </div>

          <div className='flex flex-wrap items-center gap-4'>
            {/* Input Ricerca Note */}
            <div className='flex items-center space-x-2'>
              <label className='text-sm font-medium'>Cerca nelle note:</label>
              <input
                type='text'
                placeholder='Es. Auto-approved...'
                value={notesFilter}
                onChange={handleNotesChange}
                className='flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50'
              />
            </div>

            {/* Filtro per stato */}
            <div className='flex items-center space-x-2'>
              <label className='text-sm font-medium'>Stato:</label>
              <select
                value={statusFilter}
                onChange={handleStatusChange}
                className='flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50'
              >
                <option value=''>Tutti</option>
                <option value='pending'>In Attesa</option>
                <option value='approved'>Approvati</option>
                <option value='rejected'>Rifiutati</option>
              </select>
            </div>
          </div>
        </div>

        <div className='-mx-4 mt-5 flex-1 overflow-auto px-4 py-1 lg:flex-row lg:space-y-0 lg:space-x-12'>
          {content}
        </div>
      </Main>
    </>
  )
}

export default PlayersRequestsPage
